// On hand — every number is a SUM of the stock ledger (never a stored balance).
// Receive / transfer / count all post ledger rows; applications from FieldRoutes
// deduct from the technician's location automatically.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createLocation, errorText, getOnHand, importUnitCosts, listLocations, listVehicles, postMovement, qty, updateLocation } from "./useChemicalsApi";
import "./Chemicals.css";

const LABEL = { confirmed: ["Label confirmed", "ok"], pending_review: ["Label pending review", "warn"], none: ["No label rates", ""] };

// "Truck · <vehicle> · <tech>" etc. — the same labels the phone shows, from the same on-hand endpoint.
const labelMap = (data) => {
  const m = {};
  (data?.picker_locations || []).forEach((l) => { m[l.id] = l.label; });
  (data?.by_location || []).forEach((l) => { if (l.label && !m[l.location_id]) m[l.location_id] = l.label; });
  return m;
};
const locLabel = (data, id, fallback) => labelMap(data)[id] || fallback;

function MovementForm({ data, onDone }) {
  const [kind, setKind] = useState("receive");
  const [form, setForm] = useState({ product: "", location: "", to_location: "", qty: "", counted: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const products = data?.results || [];
  const locations = data?.locations || [];
  const unit = products.find((p) => String(p.product_id) === String(form.product))?.uom || "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const out = await postMovement({ kind, ...form });
      setMsg({ ok: true, text: `Saved. On hand at that location is now ${qty(out.on_hand, unit)}.` });
      setForm((f) => ({ ...f, qty: "", counted: "", note: "" }));
      onDone();
    } catch (err) {
      setMsg({ ok: false, text: errorText(err, "Couldn't save that movement.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="chem-card">
      <div className="chem-head">
        <h2>Record a movement</h2>
        <div className="chem-seg" role="tablist">
          {[["receive", "Receive"], ["transfer", "Transfer"], ["count", "Count"]].map(([k, l]) => (
            <button key={k} type="button" className={kind === k ? "is-active" : ""} onClick={() => setKind(k)}>{l}</button>
          ))}
        </div>
      </div>
      <form className="chem-form" onSubmit={submit}>
        <label>Product
          <select value={form.product} onChange={set("product")} required>
            <option value="">Choose…</option>
            {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.product}</option>)}
          </select>
        </label>
        <label>{kind === "transfer" ? "From" : "Location"}
          <select value={form.location} onChange={set("location")} required>
            <option value="">Choose…</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{locLabel(data, l.id, l.name)}</option>)}
          </select>
        </label>
        {kind === "transfer" ? (
          <label>To
            <select value={form.to_location} onChange={set("to_location")} required>
              <option value="">Choose…</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{locLabel(data, l.id, l.name)}</option>)}
            </select>
          </label>
        ) : null}
        {kind === "count" ? (
          <label>Counted{unit ? ` (${unit})` : ""}<input type="number" step="any" min="0" value={form.counted} onChange={set("counted")} required /></label>
        ) : (
          <label>Quantity{unit ? ` (${unit})` : ""}<input type="number" step="any" min="0" value={form.qty} onChange={set("qty")} required /></label>
        )}
        <label>Note<input value={form.note} onChange={set("note")} placeholder="Optional" /></label>
        <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
      </form>
      {msg ? <div className={`chem-msg ${msg.ok ? "chem-msg--ok" : "chem-msg--err"}`} style={{ marginTop: "var(--s-3)" }}>{msg.text}</div> : null}
    </section>
  );
}

const KIND_LABEL = { warehouse: "Warehouse", vehicle: "Truck", technician: "Technician", room: "Room", bin: "Bin" };

/* Set up stock: the warehouse and one truck location per vehicle. A truck linked to a vehicle
   is where a tech's logged material comes from (the vehicle assigned to them → its truck). */
function LocationsCard({ onChanged }) {
  const [locs, setLocs] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "warehouse", vehicle: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, v] = await Promise.all([listLocations(), listVehicles().catch(() => [])]);
      setLocs(l); setVehicles(v);
    } catch (err) {
      setMsg({ ok: false, text: errorText(err, "Couldn't load locations.") });
      setLocs([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const linked = new Set((locs || []).filter((l) => l.vehicle).map((l) => String(l.vehicle)));
  const free = (keep) => vehicles.filter((v) => !linked.has(String(v.id)) || String(v.id) === String(keep));
  const stock = (locs || []).filter((l) => l.kind === "warehouse" || l.kind === "vehicle");

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const body = { name: form.name.trim(), kind: form.kind };
      if (form.kind === "vehicle" && form.vehicle) body.vehicle = Number(form.vehicle);
      const out = await createLocation(body);
      setMsg({ ok: true, text: `${out.name} added${out.vehicle_name ? `, linked to ${out.vehicle_name}` : ""}.` });
      setForm({ name: "", kind: form.kind, vehicle: "" });
      await load(); onChanged();
    } catch (err) {
      const d = err?.response?.data;
      setMsg({ ok: false, text: (d && (d.vehicle?.[0] || d.vehicle || d.name?.[0])) || errorText(err, "Couldn't add that location.") });
    } finally { setBusy(false); }
  };

  const link = async (loc, vehicleId) => {
    setBusy(true); setMsg(null);
    try {
      const out = await updateLocation(loc.id, { vehicle: vehicleId ? Number(vehicleId) : null });
      setMsg({ ok: true, text: out.vehicle_name ? `${out.name} is now ${out.vehicle_name}'s stock.` : `${out.name} is no longer linked to a vehicle.` });
      await load(); onChanged();
    } catch (err) {
      const d = err?.response?.data;
      setMsg({ ok: false, text: (d && (d.vehicle?.[0] || d.vehicle)) || errorText(err, "Couldn't link that vehicle.") });
    } finally { setBusy(false); }
  };

  return (
    <section className="chem-card">
      <div className="chem-head">
        <div>
          <h2>Stock locations</h2>
          <div className="chem-sub">Your warehouse and one truck per vehicle. Link each truck to its vehicle, and whatever the tech driving it logs on a job comes off that truck.</div>
        </div>
      </div>
      {locs === null ? <div className="chem-empty">Loading…</div> : stock.length === 0 ? (
        <div className="chem-empty">No warehouse or trucks yet. Add them below, then record what you have with a receive or a count.</div>
      ) : (
        <div className="chem-table-wrap">
          <table className="chem-table">
            <thead><tr><th>Location</th><th>Kind</th><th>Vehicle</th></tr></thead>
            <tbody>
              {stock.map((l) => (
                <tr key={l.id}>
                  <td>{l.name}</td>
                  <td>{KIND_LABEL[l.kind] || l.kind}</td>
                  <td>
                    {l.kind === "vehicle" ? (
                      <select aria-label={`Vehicle for ${l.name}`} value={l.vehicle || ""} disabled={busy} onChange={(e) => link(l, e.target.value)}>
                        <option value="">Not linked</option>
                        {free(l.vehicle).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                      </select>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <form className="chem-form" style={{ marginTop: "var(--s-3)" }} onSubmit={add}>
        <label>New location<input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Warehouse, Truck 12" /></label>
        <label>Kind
          <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value, vehicle: "" }))}>
            <option value="warehouse">Warehouse</option><option value="vehicle">Truck</option>
          </select>
        </label>
        {form.kind === "vehicle" ? (
          <label>Vehicle
            <select value={form.vehicle} onChange={(e) => setForm((f) => ({ ...f, vehicle: e.target.value }))}>
              <option value="">Link later</option>
              {free().map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
        ) : null}
        <button type="submit" className="chem-btn" disabled={busy}>Add location</button>
      </form>
      {msg ? <div className={`chem-msg ${msg.ok ? "chem-msg--ok" : "chem-msg--err"}`} style={{ marginTop: "var(--s-3)" }}>{msg.text}</div> : null}
    </section>
  );
}

/* Unit costs from a CSV: product (name, SKU or id), cost, unit. Preview shows what each row becomes
   per the product's own unit; Apply sets them all, or none if any row has an error. */
function CostImportCard() {
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const onFile = async (f) => {
    if (!f) return;
    setMsg(null); setPreview(null);
    const t = await f.text();
    setText(t); setName(f.name);
    setBusy(true);
    try { setPreview(await importUnitCosts(t, false)); }
    catch (err) { setMsg({ ok: false, text: errorText(err, "Couldn't read that file.") }); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setMsg(null);
    try {
      const out = await importUnitCosts(text, true);
      if (out.committed) { setMsg({ ok: true, text: `${out.applied} cost${out.applied === 1 ? "" : "s"} set.` }); setPreview(null); setText(""); setName(""); }
      else { setPreview(out); setMsg({ ok: false, text: out.message || "Nothing was applied." }); }
    } catch (err) { setMsg({ ok: false, text: errorText(err, "Couldn't apply those costs.") }); }
    finally { setBusy(false); }
  };
  return (
    <section className="chem-card">
      <h2>Import unit costs</h2>
      <div className="chem-sub" style={{ marginBottom: "var(--s-3)" }}>A CSV with columns <b>product, cost, unit</b>. Product can be the name, SKU or id. Unit is optional (the product's own unit); a price per gallon on a product stocked in fl oz is converted.</div>
      <div className="chem-form">
        <label>CSV file<input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} disabled={busy} /></label>
        {preview && preview.errors === 0 && preview.ok > 0 ? (
          <button type="button" className="chem-btn" onClick={apply} disabled={busy}>{busy ? "Applying…" : `Apply ${preview.ok} cost${preview.ok === 1 ? "" : "s"}`}</button>
        ) : null}
      </div>
      {msg ? <div className={`chem-msg ${msg.ok ? "chem-msg--ok" : "chem-msg--err"}`} style={{ marginTop: "var(--s-3)" }}>{msg.text}</div> : null}
      {preview ? (
        <div className="chem-table-wrap" style={{ marginTop: "var(--s-3)" }}>
          <div className="chem-detail">{name}: {preview.ok} ready{preview.errors ? `, ${preview.errors} to fix. Nothing is applied until every row is right` : ""}.</div>
          <table className="chem-table">
            <thead><tr><th>Row</th><th>Product</th><th>In the file</th><th className="num">Cost per product unit</th><th>Status</th></tr></thead>
            <tbody>{preview.rows.map((r) => (
              <tr key={r.row}>
                <td>{r.row}</td><td>{r.product || r.product_ref}</td><td>${r.cost}{r.unit ? ` per ${r.unit}` : ""}</td>
                <td className="num">{r.unit_cost != null ? `$${r.unit_cost} per ${r.product_unit}` : "—"}</td>
                <td>{r.status === "ok" ? <span className="chem-pill chem-pill--ok">Ready</span> : <span className="chem-pill chem-pill--danger">{r.error}</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

export default function OnHandPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("used");

  const load = useCallback(async () => {
    try {
      setData(await getOnHand());
      setError("");
    } catch (err) {
      setError(errorText(err, "Couldn't load on-hand."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = data?.results || [];
    if (filter === "used") return all.filter((r) => r.applications_30d > 0 || r.on_hand !== 0);
    if (filter === "low") return all.filter((r) => r.below_reorder || r.on_hand < 0);
    return all;
  }, [data, filter]);

  const negatives = (data?.results || []).filter((r) => r.on_hand < 0).length;

  return (
    <div className="chem-page">
      <div className="chem-head">
        <div>
          <h1>On hand</h1>
          <div className="chem-sub">Every number is the sum of the stock ledger. Applications logged in FieldRoutes deduct from the technician's location.</div>
        </div>
        <div className="chem-seg">
          {[["used", "In use"], ["low", "Low / negative"], ["all", "All products"]].map(([k, l]) => (
            <button key={k} type="button" className={filter === k ? "is-active" : ""} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
      </div>
      {error ? <div className="chem-msg chem-msg--err">{error}</div> : null}
      {negatives ? (
        <div className="chem-msg">
          {negatives} product{negatives === 1 ? " shows" : "s show"} negative on hand: applications were deducted but no receipt or count has been recorded yet. Record a receive or a count to set the starting stock.
        </div>
      ) : null}
      <LocationsCard onChanged={load} />
      <CostImportCard />
      <MovementForm data={data} onDone={load} />
      <section className="chem-card">
        {!data ? <div className="chem-empty">Loading…</div> : rows.length === 0 ? (
          <div className="chem-empty">Nothing to show for this filter.</div>
        ) : (
          <div className="chem-table-wrap">
            <table className="chem-table">
              <thead>
                <tr>
                  <th>Product</th><th className="num">On hand</th><th>By location</th><th className="num">Used, 30 days</th>
                  <th className="num">Applications, 30 days</th><th className="num">Days of supply</th><th className="num">Reorder point</th><th>Label</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const [lbl, tone] = LABEL[r.label_status] || LABEL.none;
                  return (
                    <tr key={r.product_id} className="chem-click" onClick={() => navigate(`/operations/inventory/products/${r.product_id}/ledger`)}>
                      <td>
                        <div>{r.product}</div>
                        <div className="chem-detail">{r.epa_reg_number ? `EPA ${r.epa_reg_number}` : "No EPA number"}</div>
                      </td>
                      <td className={`num ${r.on_hand < 0 ? "chem-neg" : ""}`}>{qty(r.on_hand, r.uom)}</td>
                      <td><div className="chem-pills">{r.by_location.map((l) => (
                        <span key={l.location_id} className={`chem-pill ${l.qty < 0 ? "chem-pill--danger" : ""}`}>{locLabel(data, l.location_id, l.location)}: {qty(l.qty, r.uom)}</span>
                      ))}</div></td>
                      <td className="num">{r.used_30d ? qty(r.used_30d, r.uom) : "—"}</td>
                      <td className="num">{r.applications_30d || "—"}</td>
                      <td className="num">{r.days_of_supply != null ? `${r.days_of_supply}` : r.on_hand <= 0 && r.used_30d > 0 ? "0" : "—"}</td>
                      <td className="num">{r.reorder_point != null ? qty(r.reorder_point, r.uom) : "Not set"}</td>
                      <td><span className={`chem-pill ${tone ? `chem-pill--${tone}` : ""}`}>{lbl}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
